// this should be re-used in all MapStateToProps functions,

import { ComponentType, NamedExoticComponent, ReactNode } from 'react';
import { type IntlShape } from 'react-intl';

// This is models.UserConfigs.
// Add any missing key.
export interface UserConfigs {
  settings: object;
}

export type AttachedContentType = 'packet' | 'custom_survey_answer';

export type AttachedContentTypes = Record<AttachedContentType, number>;

// just add fields as needed
export interface ReduxState {
  me: Me;
  authUser: AuthUser;
  features: Features;
  campaigns: Campaign[];
  currentOrganization: Organization;
  originalOrganization: Organization;
  currentProxyPerson: Person;
  currentProxyOrganization: Organization;
  myConfigs: UserConfigs;
  settings: OrganizationSettings;
  attachedContentTypes: AttachedContentTypes;
  visibleAgents: VisibleAgent[];
  isUnableToLoadDueToError: boolean;
  isLoadingApp: boolean;
  orgHasLevelingFramework: boolean;
  currentPerfSurveyResponse: any;
  demoPeople: any[];
  isTesting?: boolean;
  roles: PersonRoleInitApp[];
}

// Flexible type for redux actions
export type Action<T extends string, P> = {
  type: T;
} & P;

// Common Types

export interface VisibleAgent {
  id: number;
  name: string;
  description: string;
}

export interface AgendaItem {
  id: string;
  key: string;
  title: string;
  created_at: string;
  completed_at?: string;
  sort_key?: string;
}

interface App {
  square_image: string | null;
  display_name: string;
  categories: Array<string> | null;
  color: string | null;
  id: number;
  image: string | null;
  internal_name: string;
}

export interface AppIntegration {
  id: number;
  app: App;
  last_synced_on?: string;
  created_at: string;
}

export interface AuthUser {
  sub: string;
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  'https://app.confirmhr.com/hris_id': string;
  'https://app.confirmhr.com/roles': Array<string>;
  'https://example.com/geoip': object;
  locale: string;
  name: string;
  nickname: string;
  picture: string;
  updated_at: string;
}

interface CampaignProperties {
  has_engagement_survey: boolean;
  is_engagement_survey_only: boolean;
  has_survey: boolean; // if has engagement survey or ENPS
}

export interface CustomerDataColumnOwner {
  id: number;
  full_name: string;
  email: string;
}

export interface CustomerDataColumn {
  key: string;
  label: string;
  owner_persons?: CustomerDataColumnOwner[];
}

export interface Campaign {
  id: number;
  name: string;
  phases: Phase[];
  coverage_start_date: string;
  coverage_end_date: string;
  properties?: CampaignProperties;
  wizard_type?: string;
  campaign_question_locks?: { [key: string]: string[] };
  customer_campaign_data_columns?: CustomerDataColumn[];
}

export interface CampaignConfigs {
  introduction_ui_phase?: object;
  hidden_phase_S?: object;
  hidden_phase_O?: object;
  hidden_phase_E?: object;
  hidden_phase_C?: object;
  hidden_phase_R?: object;
  created_with_default_questions?: boolean;
}

export interface CampaignWithConfigs extends Campaign {
  dataset_id?: number;
  configs: CampaignConfigs;
}

export type NewCampaign = Omit<Campaign, 'id'>;

export interface NewCampaignWithConfigs extends NewCampaign {
  configs: CampaignConfigs;
}

type DatasetType = 'P';
export interface Dataset {
  data: DatasetRecord[];
  id: number;
  name: string;
  type: DatasetType;
  mappings: DatasetMappings;
}

export interface RegenerationDataset extends Dataset {
  can_regenerate_participants: boolean;
}

export interface DatasetRecord {
  email: string;
  is_artificially_added?: boolean;
}

export interface DatasetMappings {
  admins: { [key: string]: unknown };
  keys: { [key: string]: string };
  transforms: { [key: string]: unknown };
  values: { [key: string]: unknown };
}

export interface SurveyResponseConfigs {
  is_participating?: boolean;
  is_participating_in_self_phase?: boolean;
  is_only_receiving_review?: boolean;
  latest_hire_date?: string;
  is_rated?: boolean;
  position_effective_date?: string;
  position_start_date?: string;
  original_hire_date?: string;
  [key: string]: unknown;
}
export interface SurveyResponse {
  configs: SurveyResponseConfigs;
  responses: object;
  highlights?: object[];
  released_at?: string;
}

export interface Feedback {
  comments: string;
  author_person: Person;
  // single-letter FeedbackType code ('E' recognition, 'A' actionable, 'N' note)
  type: string;
}

export type FeedbackType = {
  id: string;
  name: string;
  title: string;
  heading: string;
  subheading: string;
  icon: string;
  description: string;
};

export enum PersonProfileDeltasMode {
  ALL = 'all',
  EXCEPT_SELF = 'except-self',
  OFF = 'off',
}

export interface RouteConfig {
  name: string;
  path: string;
}

export type RouteConfigFn = (
  formatMessage: IntlShape['formatMessage']
) => RouteConfig;

export type RouteConfigFnWithDefault = (
  formatMessage?: IntlShape['formatMessage']
) => RouteConfig;

type EnabledFlag = {
  enabled: boolean;
};

// For feature display in the organization features list
// that system admins and superadmins can see
export interface Feature {
  name: string;
  display_name: string;
  enabled: boolean;
  description: string;
  category: string;
  superuser_only: boolean;
  controls_category: boolean;
}

export interface Features {
  performance: EnabledFlag & {
    surveys_and_performance: boolean;
    complete_survey: boolean;
    rating_dashboard: boolean;
    team_dashboard: boolean;
    participation_dashboard: boolean;
    calibration_dashboard: boolean;
    key_takeaways: boolean;
    performance_profiles: boolean;
    customer_campaign_data: boolean;
  };
  content_administration: EnabledFlag & {
    templates_enabled: boolean;
  };
  engagement: EnabledFlag;
  people: EnabledFlag & {
    org_chart: boolean;
    list_view: boolean;
  };
  ai_assistant: EnabledFlag & {
    ai_agents: boolean;
    ai_canned_demo_responses: boolean;
    ai_performance_self_reflection: boolean;
    ai_performance_self_activity_generator: boolean;
    ai_performance_manager_review_direct_report: boolean;
    ai_feedback_recognition: boolean;
    ai_enable_attachments: boolean;
  };
  additional_managers: EnabledFlag;
  objectives: EnabledFlag & {
    version_20: boolean;
    rollup_enabled: boolean;
    show_in_dashboard: boolean;
    tags_enabled: boolean;
    admins_enabled: boolean;
    objective_types_enabled: boolean;
  };
  continuous_feedback: EnabledFlag & {
    recognition_enabled: boolean;
    private_notes_enabled: boolean;
    giving_feedback_enabled: boolean;
    show_in_dashboard: boolean;
    feedback_requests_enabled: boolean;
    feedback_request_custom_questions_enabled: boolean;
    noted_visible_to_hr_admins: boolean;
  };
  talent_matrices: EnabledFlag;
  unified_intercom: EnabledFlag;
  external_manager_ratings: EnabledFlag;
  activities: EnabledFlag & {
    show_in_dashboard: boolean;
  };
  pulse_checks: EnabledFlag & {
    show_in_dashboard: boolean;
  };
  promotion_packets: EnabledFlag & {
    feedback_requests_enabled: boolean;
  };
  autosave_forms: EnabledFlag;
  skills_and_behaviors: EnabledFlag & {
    list_view: boolean;
    people_list: boolean;
    activity_list: boolean;
    profile_word_clouds: boolean;
  };
  skills_talent_inventory: EnabledFlag;
  people_admin_admins_beta: EnabledFlag;
  profile_intro: EnabledFlag;
  operating_manual: EnabledFlag & {
    required: boolean;
  };
  development_plans: EnabledFlag;
  engagement_survey_reports: EnabledFlag;
  extended_calibration: EnabledFlag;
  one_on_ones_administration: EnabledFlag;
  performance_profile_enable_charts: EnabledFlag;
  performance_profile_enable_distributions: EnabledFlag;
  cycle_admin: EnabledFlag & {
    peer_360_options_enabled: boolean;
  };
  resumes: EnabledFlag;
  one_on_ones: EnabledFlag;
  goals: EnabledFlag;
  notifications: EnabledFlag & {
    enabled_email: boolean;
    enabled_slack: boolean;
    enabled_msteams: boolean;
  };
  nudges: EnabledFlag;
  credentials: EnabledFlag;
  awards: EnabledFlag;
  allow_feedback_requests_when_claiming_contributions: EnabledFlag;
  async_dashboard: EnabledFlag;
  levels: EnabledFlag;
  team_overview_dashboard: EnabledFlag;
  ai_recommendations: EnabledFlag;
  edit_hr_data: EnabledFlag;
  automatic_feedback_requests_on_manager_change: EnabledFlag;
  allow_non_perf_im_notifications: EnabledFlag;
  allow_calendar_integration: EnabledFlag;
  hide_former_employees: EnabledFlag;
  integration_learning_scaleup: EnabledFlag;
  solicit_g2_reviews: EnabledFlag;
  auto_redirect_to_perf: EnabledFlag;
  people_csv_importer: EnabledFlag;
  people_platform_id: EnabledFlag;
  engagement_survey_summaries: EnabledFlag;
  referrals: EnabledFlag;
  shareable_reports_ai_summaries: EnabledFlag;
  custom_surveys: EnabledFlag & {
    custom_survey_reports: boolean;
  };
  ona_reporting: EnabledFlag & {
    show_influence: boolean;
    show_gold_stars: boolean;
    show_heads_ups: boolean;
  };
}

export interface InitOrganization {
  id: number;
  name: string;
  email_domains?: string[] | null;
  status: string;
  custom_session_ttl: number;
  instant_messaging: string;
  suspended_message?: string | null;
}

export interface InitProxyPerson {
  full_name: string;
  user_id: number;
  id: number;
  avatar: string;
  title?: null;
  given_name: string;
  family_name: string;
  email: string;
  url: string;
  status: string;
}

export interface DirectReportsEntity {
  id: number;
  email: string;
  given_name?: string | null;
  family_name: string;
  full_name: string;
  url: string;
  avatar?: string | null;
  title: string;
  status: string;
}

export interface Manager {
  id: number;
  email: string;
  given_name: string;
  family_name: string;
  full_name: string;
  url: string;
  avatar: string;
  title: string;
  status: string;
}

export interface Organization {
  id?: number;
  name: string;
  status?: string;
  is_hrbp?: boolean;
  is_one_on_one_admin?: boolean;
  is_objectives_admin?: boolean;
  is_promotion_packet_admin?: boolean;
  is_development_plan_admin?: boolean;
  is_ai_admin?: boolean;
  is_system_admin?: boolean;
  is_super_admin?: boolean;
  is_impersonating?: boolean;
  instant_messaging?: string;
  email_domains?: string[];
  suspended_message?: string | null;
}

export interface BasicPerson {
  id?: number;
  email?: string;
  given_name: string;
  family_name: string;
  full_name: string;
  avatar: string;
  title: string;
}

export interface AnonymousPerson extends BasicPerson {
  id: undefined;
  email: undefined;
}

export interface Person extends BasicPerson {
  id: number;
  email: string;
  url: string;
  status: string;
  is_customer_provisioned?: boolean;
}

export interface PotentiallyAdminablePerson extends Person {
  is_adminable?: boolean;
  is_below_in_chain_of_command?: boolean;
}

// Matches AdminOnlyPersonSerializer from the backend
export interface AdminOnlyPersonMetadata {
  cost_center?: string;
  cost_center_id?: string;
  country?: string;
  level?: string;
  level_id?: string;
  termination_date?: string | null;
  chain_of_command_list?: BasicPerson[];
}

export interface PersonForTeamOverview
  extends BasicPerson,
    AdminOnlyPersonMetadata {
  operating_manual_percent_complete: number;
  most_recent_feedback_at: string;
  most_recent_aspiration_at: string;
  most_recent_goal_at: string;
  most_recent_objective_at: string;
  last_pulse_check_at: string;
  last_pulse_check_rating: number;
  last_pulse_check_comments: string;
}

// Matches ExtendedPersonSerializer from the backend, but
// including AdminOnlyPersonMetadata so we properly render
// whether or not the given viewer has those additional
// fields viewable to them
export interface ExtendedPerson
  extends PotentiallyAdminablePerson,
    AdminOnlyPersonMetadata {
  user_id: number;
  preferred_given_name: string;
  legal_given_name: string;
  profile: Profile;
  is_below_in_chain_of_command: boolean;
  is_direct_report_in_campaign: boolean;
  is_adminable: boolean;
  is_active: boolean;
  latest_hire_date: string;
  function?: string;
  location: string;
  home_location?: null;
  department?: string;
  business_unit?: string;
  direct_reports?: DirectReportsEntity[] | null;
  manager: Manager | null;
  declared_skills?: null[] | null;
  declared_credentials?: null[] | null;
  credential_issuances?: null[] | null;
  award_issuances?: null[] | null;
  locale?: string | null;
  slug?: string | null;
  business_unit_id?: string;
  department_id?: string;
}

export interface TeamDashboardAMDraftRating {
  manager: BasicPerson;
  draft_rating?: string;
  draft_rating_comments?: string;
}
export interface TeamDashboardAMRecommendForPromotion {
  manager: BasicPerson;
  recommend_for_promotion?: boolean;
  promotion_comments?: string;
}

export interface TeamDashboardAMRecommendForSalaryIncrease {
  manager: BasicPerson;
  recommend_for_salary_increase?: boolean;
  salary_increase_comments?: string;
}

export enum PerformanceProgressCompletion {
  BELIEVED_ADDITIONAL_MANAGERS = 'believed_additional_managers',
}

export interface TeamDashboardPerson extends BasicPerson {
  manager?: Person;
  additional_managers: Person[];
  previous_managers: Person[];
  believed_manager?: Person;
  believed_additional_managers: Person[];
  draft_rating_provided_by?: Person;
  draft_rating_comments?: string;
  draft_rating_additional_managers: TeamDashboardAMDraftRating[];
  recommend_for_promotion?: boolean; // this contains if is answered yes OR has a promo package
  extracted_recommend_for_promotion?: boolean; // this is the pure answer from the manager
  promotion_comments?: string;
  recommend_for_promotion_additional_managers: TeamDashboardAMRecommendForPromotion[];
  packet?: any;
  recommend_for_salary_increase?: boolean;
  salary_increase_comments?: string;
  recommend_for_salary_increase_additional_managers: TeamDashboardAMRecommendForSalaryIncrease[];
  perf_progress_completions: Set<PerformanceProgressCompletion>;
  can_edit_final_rating?: boolean;
}

// Me from backend uses ExtendedPersonSerializer, but
// we define here separately to avoid confusion and
// allow for future changes to just me that won't
// affect ExtendedPerson
export interface Me extends ExtendedPerson {}

export interface ContactMethod {
  id: string;
  value: string;
}

export interface Profile {
  intro?: string;
  last_development_conversation_at?: string; // format: 2024-09-02T07:00:00Z
  most_reachable_via?: ContactMethod[];
  name_pronunciation?: string;
  operating_manual?: Record<string, string>;
  pronouns?: string;
  wants_to_mentor_since?: string; // format: 2024-09-03T22:26:43.742000Z
}

// Keep in sync with RELATIONSHIP_TYPE in Relationship.js
export enum RelationshipType {
  REPORTS_TO = 'R',
  USED_TO_REPORT_TO = 'U',
  USED_TO_HAVE_AS_DIRECT_REPORT = 'T',
  ENERGIZED_BY = 'E',
  ADVISED_BY = 'A',
  HAS_AS_STAKEHOLDER = 'S',
  GIVES_GOLD_STAR_TO = 'G',
  GIVES_HEADS_UP_ABOUT = 'H',
  NEEDS_AS_HIGH_PRIORITY_PEER = 'P',
  IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR = 'I',
  HAS_DIRECT_REPORT_FEEDBACK_FOR = 'D',
  HAS_ADDITIONAL_MANAGER_DIRECT_REPORT_FEEDBACK_FOR = 'F',
  SELF = 'L',
}

export interface Relationship {
  id: number;
  created_at?: string;
  from_person: Person;
  to_person?: Person;
  is_anonymous?: boolean;
  positive_skills?: Skill[];
  positive_comments?: string;
  negative_skills?: Skill[];
  negative_comments?: string;
  type: RelationshipType;
  rating_comments?: string;
  ona_source_type?: string;
  rating?: number;
  final_rating?: number;
  final_rating_comments?: string | null;
  final_rating_provided_by_person?: Person | null;
  responses?: { [key: string]: string | undefined };
  recommend_for_promotion?: boolean;
  promotion_comments?: string;
  decline_reason?: string;
  priority?: number;
}

export interface PromotionPacketInfo {
  id: number;
  status: string;
}

export interface PromotionPacket {
  id: number;
  status: string;
  is_adminable: boolean;
  is_admin_or_above: boolean;
  candidate_person: Person;
  author_person: Person;
}

export interface Skill {
  id?: string | number;
  name: string;
  type: string;
}

export interface Tab {
  path: string;
  name: string | JSX.Element;
  content: JSX.Element;
  action?: JSX.Element;
}

// ElasticSearch Responses

export interface ElasticSearchResponse<Source = unknown> {
  took: number;
  timed_out: boolean;
  _shards: Shards;
  hits: Hits<Source>;
}

export interface Hit<Source = unknown> {
  _index: string;
  _type: string;
  _id: string;
  _score: number | null;
  _source: Source;
  sort: number[];
}

export interface Hits<Source = unknown> {
  total: Total;
  max_score: number | null;
  hits: Hit<Source>[];
}

export interface Shards {
  total: number;
  successful: number;
  skipped: number;
  failed: number;
}

export interface Total {
  value: number;
  relation: string;
}

// ConfirmAPI Responses

export interface CurrentActiveResponse {
  campaign?: Campaign;
  phase_permissions?: Record<string, unknown>;
  completions?: Record<string, unknown>;
  is_first_time_participant?: boolean;
}

export interface OrgChartRelatives {
  manager: Person | null;
  additional_managers: Array<Person>;
  above_manager: Array<Person>;
  peers: Array<Person>;
  direct_reports: Array<Person>;
}

export interface Quarter {
  start: string;
  end: string;
}

export type TimeFrameType = 'QUARTER' | 'YEAR';

export type TimeFrame = {
  type: TimeFrameType;
  start: Date;
  end: Date;
};

export type ApiTimeFrame = {
  type: TimeFrameType;
  start: string; // yyyy-MM-dd
  end: string; // yyyy-MM-dd
};

export interface InternationalizationSettings {
  enabled: boolean;
  default_locale: string;
  supported_locales: string[];
}

export interface AgendaItemTemplateDescriptor {
  id: string | number;
  name: string;
  value: string | string[];
  category?: string;
}

export interface DevelopmentPlanTemplateDescriptor {
  id: string;
  name: string;
  value: string;
  deprecated?: boolean;
  candidate_can_manage?: boolean;
}

export type ISOCurrencyCode = string;

export interface ObjectivesCurrenciesConfig {
  default: ISOCurrencyCode;
  allowed: ISOCurrencyCode[];
}

// TODO: add more settings as you use this across the app
export interface OrganizationSettings {
  objectives_currencies: ObjectivesCurrenciesConfig;
  objectives_restrict_visibility_to_match_editability?: boolean;
  objectives_timeframe: TimeFrameType;
  objectives_timeframe_default_start_date: string;
  disable_enps_full_export: boolean;
  company_objectives_link?: string;
  agenda_item_templates?: AgendaItemTemplateDescriptor[];
  feedback_request_templates?: string[];
  internationalization: InternationalizationSettings;
  development_plans_templates: DevelopmentPlanTemplateDescriptor[];
  default_promotion_packet_questions?: any[];
  default_promotion_include_description_in_csv_exports?: boolean;
  default_promotion_packet_description?: string;
  default_promotion_effective_date?: string;
  default_promotion_level_options?: any[];
  default_promotion_url_input_attributes?: any[];
  default_promotion_approvers?: object[];
  promotion_packet_custom_questions?: object[];
  performance_profile_deltas_mode: PersonProfileDeltasMode;
  skills_restrict_organization_only?: boolean;
}

export interface Task {
  time?: Date;
  timeText?: ReactNode;
  sender?: Person;
  listDescription?: string;
  acceptText?: string;
  declineText?: string;
  type: string;
  object?: {
    id: number;
  };
  acceptPopoverContent?: string;
  declinePopoverContent?: string;
  popupDescription?: string;
}

export interface Comment {
  created_at: string;
  content_type: number;
  object_id: number;
  parent_comment_id?: number;
  author_person: Person;
  on_behalf_of_person?: Person;
  type: string;
  body: string;
}

export interface Reaction {
  author_person: Person;
  content_type: number;
  object_id: number;
  emoji: string;
}

export interface ContributionComment {
  id: number;
  created_at: string;
  updated_at: string;
  author_person: Person;
  contributor_role: string;
  description: string;
  skills: Skill[];
  reactions: Reaction[];
}

export interface Contribution {
  id: number;
  contributor_person: Person;
  description?: string;
  skills: Skill[];
  contribution_comments: ContributionComment[];
  reactions: Reaction[];
  requested_people?: Person[] | null;
}

export interface Activity {
  id: number;
  organization: number;
  created_at: string;
  updated_at: string;
  date_started?: string | null;
  date_completed?: string | null;
  type: string;
  stage: string;
  visibility: string;
  parent?: number | null;
  name: string;
  description: string;
  contributions: Contribution[];
  reactions: Reaction[];
}

export interface PulseCheck {
  id: number;
  created_at: string;
  updated_at: string;
  person: Person;
  rating: number;
  rating_comments?: string;
}

export type Media = {
  created_at: string;
  created_by: number;
  file: string;
  id: number;
  parsed_json: null;
  purpose: string;
  updated_at: string;
  uuid: string;
};

export type MediaResponse = {
  link: string;
  media: Media;
  description?: string;
};

export interface ColumnConfig {
  name: string | ReactNode;
  field: string;
  sortable: boolean;
  popoverContent: ReactNode;
  type: string;
  generateDefaultValue?:
    | ((value: unknown) => string)
    | ((length: number, value: unknown) => number);
  jsonPath?: string;
  jsonPathItemGenerator?: (basePath: string, obj: unknown) => string;
  formatValueOnSubmit?:
    | ((value: unknown) => string)
    | ((value: unknown) => number | undefined);
  suspendFormatValueOnSubmit?: ((value: unknown) => boolean) | boolean;
  component?: ReactNode | NamedExoticComponent<any>;
  columnClassName?: string;
  filterOnly?: boolean;
  showFullLengthHeader?: boolean;
}

export type ShouldShowAIAssistedQuestionDescriptorType = (
  questionType: string,
  aiAssisted?: boolean | null,
  evaluationPhaseEvaluationAIAssistedQuestions?: boolean | null
) => boolean;

export interface ValidatedInputConfig {
  name: string;
  label?: string | ReactNode;
  helperText?: string | ReactNode;
  type: string;
  required?: boolean;
  placeholder?: string | number;
  options?: Array<{ value: string; label: string }>;
  validation?: string;
  validationMessage?: string;
  defaultValue?:
    | string
    | number
    | boolean
    | ((context: CampaignFlowContextType) => object);
  formGroupClassName?: string;
  formGroupStyle?: object;
  format?: string;
  autoFocus?: boolean;
  onChangeSideEffects?: (
    currentValue: object,
    newValue: object,
    intl: IntlShape,
    organizationSettings: OrganizationSettings,
    name: string
  ) => object;
  preserveExistingAnswers?: boolean;
  objects?: object[];
  addText?: string | ReactNode;
  columns?: ColumnConfig[];
  quarterHelperText?: string | ReactNode;
  disableOnChange?: boolean;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  disabled?: boolean;
  canBePrivate?: boolean;
  canAllowCalibration?: boolean;
  canBeAnonymous?: boolean;
  jsonRoot?: string;
  enableTime?: boolean;
  showActions?: boolean;
  inputType?: string;
  minValue?: number;
  component?: ReactNode | NamedExoticComponent<any> | ComponentType<any>;
  excludeSpecialQuestions?: string[];
  allowedQuestionTypes?: string[];
  disableTypeChange?: boolean;
  typeChangeDisabledMessage?: string;
  includeExcludeLabel?: string;
  displayFactor?: boolean;
  canHaveAiAssistedQuestions?: boolean;
  aiAssistedQuestionsVisibilityField?: string;
  shouldShowAIAssistedQuestionDescriptor?: ShouldShowAIAssistedQuestionDescriptorType;
  availableSectionsDescriptor?: QuestionEditorAvailableSectionsDescriptor;
  employeeNpsCommentTitle?: string; // Used for EmployeeNPSInput
  secondaryToggle?: {
    name: string;
    label?: string | ReactNode;
    helperText?: string | ReactNode;
    hidden?: boolean;
    disabled?: boolean;
    defaultSecondaryToggleValue: boolean;
  };
}

export type CalibrationItem = {
  id: number;
  key: string;
  value: unknown;
  comment: string;
  created_at: Date;
  author: Person;
  survey_response: number;
};

export type CalibrationItemWithDisplay = CalibrationItem & {
  displayValue: string;
};

export type CalibrationOriginalItem = {
  value: unknown;
  author: Person;
  priority: number;
  comment?: string;
};

export type CalibrationInfo = {
  surveyResponseId: number;
  currentValue: unknown;
  priorityCurrentValue: unknown;
  originalResponses: CalibrationOriginalItem[];
  calibrationItems: CalibrationItemWithDisplay[];
};

export type Question =
  | TextArea
  | RichTextEditor
  | PeopleEditor
  | MultipleChoiceQuestion
  | LikertQuestion
  | YesNo
  | DropdownQuestion;

export type MultipleChoiceQuestion = {
  key: string;
  type: 'MULTIPLE_CHOICE';
  options: Array<{ id: string; name: string }>;
};

export type LikertScaleItem = {
  name: string;
  value: number;
};

export type LikertSubItems = {
  name: string;
  value: string;
};

export type LikertQuestion = {
  key: string;
  type: 'LIKERT';
  scale: LikertScaleItem[];
  objects: LikertSubItems[];
};

export type DropdownItem = {
  id: string;
  name: string;
};

export type DropdownQuestion = {
  key: string;
  type: 'DROPDOWN';
  objects: DropdownItem[];
};

export type RichTextEditor = {
  key: string;
  type: 'RICH_TEXT_EDITOR';
  value: string;
};

export type TextArea = {
  key: string;
  type: 'TEXTAREA';
  value: string;
};

export type PeopleEditor = {
  key: string;
  type: 'PEOPLE_EDITOR';
  value: Person[];
};

export type YesNo = {
  key: string;
  type: 'YES_NO';
  value: boolean;
};

export interface CollapsiblePhaseConfig {
  type: string;
  title: string | ReactNode;
  helperText: string | ReactNode;
  inputs: ValidatedInputConfig[];
  disableToggle?: boolean;
  excludeSpecialQuestions?: string[];
  questionDefaults?: Record<string, unknown>;
  canHaveAiAssistedQuestions?: boolean;
  aiAssistedQuestionsVisibilityField?: string;
  shouldShowAIAssistedQuestionDescriptor?: ShouldShowAIAssistedQuestionDescriptorType;
}

export interface CampaignFlowContextType {
  phaseSelfSetObjectivesLegacy: boolean;
  phaseSelfStartDate: string;
  ratingScale: number[];
  objectivesOrganizationTimeFrameType: TimeFrameType;
  objectivesOrganizationTimeFrameDefaultStartDateMMDD: string;
}

export interface CampaignEditorPhaseContextType {
  phaseType: string;
  campaignId: number;
  enabledPhase: Record<string, boolean>;
}

export interface CampaignAIAssistedContextType {
  currentPhase: Phase;
  phaseCustomQuestionGroupName: string;
  shouldShowAIAssistedQuestionDescriptor: ShouldShowAIAssistedQuestionDescriptorType;
}

export type PreviewMode = 'CAMPAIGN_EDITOR' | 'QUESTION_EDITOR';

export interface ObjectDropdownOption {
  id: string;
  name: string;
}

export type RatingScale = number[];

export type RatingTarget = 'positive' | 'negative' | 'neutral' | number[];
export type DeltaDirection = 'up' | 'down' | 'both';
export type CalibrationEventType = 'hired' | 'role_change' | 'promotion';
export type OnaCounter = 'heads_ups' | 'gold_stars' | 'advises' | 'energizes';
export type ThresholdOperator = 'gte' | 'lte' | 'eq';
export type CalibrationPeriodType =
  | 'cycles'
  | 'years'
  | 'quarters'
  | 'months'
  | 'days';

export interface DeltaCondition {
  type: 'delta';
  delta: number;
  direction: DeltaDirection;
}

export interface ConsecutiveCondition {
  type: 'consecutive';
  target: RatingTarget;
}

export interface ManagerRatingsCondition {
  type: 'manager_ratings';
  target: RatingTarget;
}

export interface EventCondition {
  type: 'event';
  event: CalibrationEventType;
  target: RatingTarget;
}

export interface OnaCondition {
  type: 'ona';
  ona: OnaCounter | OnaCounter[];
  threshold: number;
  threshold_operator?: ThresholdOperator;
  target: RatingTarget;
}

export type AutoCalibrationRuleCondition =
  | DeltaCondition
  | ConsecutiveCondition
  | ManagerRatingsCondition
  | EventCondition
  | OnaCondition;

export type AutoCalibrationConditionType = AutoCalibrationRuleCondition['type'];

export interface AutoCalibrationRulePeriod {
  type: CalibrationPeriodType;
  duration: number;
}

export interface AutoCalibrationRule {
  template: string;
  condition: AutoCalibrationRuleCondition;
  period: AutoCalibrationRulePeriod;
  // Flags are stored as rule indices, so a disabled rule keeps its slot
  // instead of being removed. Missing means enabled.
  enabled?: boolean;
}

export interface Phase {
  type: string;
  auto_calibration_rules?: AutoCalibrationRule[];
  was_auto_deactivated?: boolean;
  name?: string;
  description?: string;
  assess_organization?: boolean;
  employee_nps_question_enabled?: boolean;
  disable_network?: boolean;
  disable_callouts?: boolean;
  assess_manager?: boolean;
  peer_feedback?: boolean;
  start_date?: string;
  end_date?: string;
}

export enum Role {
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  HR_ADMIN = 'HR_ADMIN',
  ENGAGEMENT_SURVEY_ADMIN = 'ENGAGEMENT_SURVEY_ADMIN',
  RATING_DASHBOARD_ADMIN = 'RATING_DASHBOARD_ADMIN',
  DEVELOPMENT_PLAN_ADMIN = 'DEVELOPMENT_PLAN_ADMIN',
  ONE_ON_ONE_ADMIN = 'ONE_ON_ONE_ADMIN',
  AI_ADMIN = 'AI_ADMIN',
  OBJECTIVES_ADMIN = 'OBJECTIVES_ADMIN',
  PROMOTION_PACKET_ADMIN = 'PROMOTION_PACKET_ADMIN',
  CALIBRATION_VIEW_EDITOR = 'CALIBRATION_VIEW_EDITOR',
}

export type PersonRoleInitApp = {
  role: Role;
  config?: {
    target_development_plans?: string[];
    calibration_view_admin?: boolean;
    target_campaigns?: number[];
  };
};

export enum AttributionVisibility {
  ATTRIBUTED = 'attributed',
  UNATTRIBUTED = 'unattributed',
  // unattributed to the subject of the feedback, but attributed to
  // the subject's manager(s) and above, and their HR admins
  UNATTRIBUTED_TO_SUBJECT = 'unattributed-to-subject',
  // unattributed to the subject of the feedback, their manager and above, but attributed to
  // and their HR admins
  UNATTRIBUTED_TO_SUBJECT_AND_CHAIN_OF_COMMAND = 'unattributed-to-subject-and-chain-of-command',
}

// ----------------------------------------
// From here till the closing comment, there is a lot
// of guess work on these types. There is a chance they
// are not sematically correct but for now we are just
// extracting type information to please the typechecker.
// In a second phase we can revisit and define types
// that actually make sense.

export interface CampaignQuestionObjectsType {
  name: string;
  value?: any;
  id?: string;
  helperText?: string;
}

export interface QuestionEditorAvailableSection {
  id: string;
  name: string;
  helperText?: string;
}

export interface QuestionEditorAvailableSectionsDescriptor {
  sections: QuestionEditorAvailableSection[];
  helperHover: string;
}

export interface CampaignQuestion {
  canBeAnonymous?: boolean;
  canBePrivate?: boolean;
  canAllowCalibration?: boolean;
  filter?: object[];
  helperText?: string;
  helperTextLearnMorePopover?: string;
  label: string;
  maxLength?: number;
  maxRows?: number;
  minRows?: number;
  name?: string;
  objects?: CampaignQuestionObjectsType[];
  organization?: number;
  required?: boolean;
  scale?: object[];
  section?: string;
  type?: string;
}

export interface QuestionDefaults {
  positive_skills?: object;
  negative_skills?: object;
  positive_comments?: object;
  negative_comments?: object;
  rating?: object;
}

export interface QuestionOptions {
  type: string;
  label?: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  minRows?: number;
  maxRows?: number;
  objects?: object[];
}

export interface QuestionType extends QuestionDefaults {
  name: string;
  heading: string;
  subheading: string;
  icon: string;
  description: string;
  generateQuestionId: boolean;
  allowedQuestionTypes?: string[];
  questionOptions?: QuestionOptions;
}

export interface UserGeneratedInputTypeAttribute {
  columns?: object[];
  defaultValue?: any;
  displayIf?: (question) => boolean;
  formatValueOnSubmit?: (value) => any;
  inputType?: string;
  jsonPath?: string;
  label: string;
  minValue?: number;
  name: string;
  placeholder?: string;
  translationDisabled?: boolean;
  translationDisabledMessage?: ReactNode;
  translationNamespace?: string;
  type: string;
}

// There is a chance that this type and CampaginQuestion
// are ultimately the same type.
export interface UserGeneratedInputType {
  canBeAnonymous?: boolean | null;
  canBePrivate?: boolean | null;
  canAllowCalibration?: boolean | null;
  attributes?: UserGeneratedInputTypeAttribute[];
  format?: string;
  id?: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  name?: string;
  section?: string;
  type?: string;
  required?: boolean;
  anonymous?: boolean;
  hide_from_recipient?: boolean;
  allow_calibration?: boolean;
  factor?: string;
  filters?: { include?: unknown[]; exclude?: unknown[] };
  // Likert scale heading labels; kept loose (matches CampaignQuestion.scale)
  scale?: object[];
  // Sometimes used as required, sometimes optional;
  objects?: CampaignQuestionObjectsType[];
  // Sometimes used as required, sometimes optional;
  options?: {
    id: string;
    name: string;
    helperText: string;
  }[];
  // Code does parseInt on it.
  minSelections?: string;
  // Code does parseInt on it.
  maxSelections?: string;
  maxLength?: number;
}

// Closing comment.
// ----------------------------------------
